'use client';

import { useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { useTranslations } from 'next-intl';
import type { ActiveFilters, FilterCategories, FilterKey } from './filter-types';
import { FILTER_KEYS } from './filter-types';

interface FilterPanelProps {
  categories: FilterCategories;
  activeFilters: ActiveFilters;
  onToggle: (key: FilterKey, handle: string) => void;
  /** Group refs the desktop sidebar scrolls to when "View all" is clicked. */
  groupRefs?: Partial<Record<FilterKey, React.RefObject<HTMLDivElement | null>>>;
  /** Forces one group open even if the shopper collapsed it (used by "View all"). */
  forceOpenKey?: FilterKey | null;
}

/**
 * The filter groups (subjects / styles / rooms / colors) rendered as
 * collapsible sections of pill toggles. Shared, layout-agnostic content used
 * inside both the desktop sidebar and the mobile bottom sheet so the two
 * never drift apart. Groups default open; a group with active selections
 * always stays open regardless of its collapsed state.
 */
export function FilterPanel({
  categories,
  activeFilters,
  onToggle,
  groupRefs,
  forceOpenKey,
}: FilterPanelProps) {
  const t = useTranslations('shop.filterBar');
  const [collapsed, setCollapsed] = useState<Partial<Record<FilterKey, boolean>>>({});

  const categoryLabels: Record<FilterKey, string> = {
    subjects: t('categories.subjects'),
    styles: t('categories.styles'),
    rooms: t('categories.rooms'),
    colors: t('categories.colors'),
  };

  return (
    <div className="space-y-5">
      {FILTER_KEYS.map(key => {
        const options = categories[key] ?? [];
        if (options.length === 0) return null;
        const selectedCount = activeFilters[key]?.length ?? 0;
        const isOpen = selectedCount > 0 || forceOpenKey === key || !collapsed[key];
        const panelId = `filter-panel-${key}`;

        return (
          <div key={key} ref={groupRefs?.[key]} className="border-b border-gray-100 pb-5 last:border-0 last:pb-0 dark:border-white/5">
            <button
              type="button"
              aria-expanded={isOpen}
              aria-controls={panelId}
              onClick={() => setCollapsed(prev => ({ ...prev, [key]: isOpen }))}
              className="flex min-h-9 w-full items-center justify-between gap-2 text-left text-sm font-semibold text-gray-900 dark:text-white"
            >
              <span className="flex items-center gap-2">
                {categoryLabels[key]}
                {selectedCount > 0 && (
                  <span className="rounded-full bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold leading-none text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                    {selectedCount}
                  </span>
                )}
              </span>
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isOpen && (
              <div id={panelId} className="mt-3 flex flex-wrap gap-2">
                {options.map(option => {
                  const isSelected = activeFilters[key]?.includes(option.handle);
                  return (
                    <button
                      key={option.handle}
                      type="button"
                      aria-pressed={isSelected}
                      onClick={() => onToggle(key, option.handle)}
                      className={`min-h-9 rounded-full px-3 py-1.5 text-sm transition-colors ${
                        isSelected
                          ? 'bg-amber-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-slate-800 dark:text-gray-300 dark:hover:bg-slate-700'
                      }`}
                    >
                      {option.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
